import type {
	ColumnOrderState,
	ColumnSizingState,
	VisibilityState,
} from "@tanstack/react-table";
import { DEFAULT_COLUMN_ORDER, DEFAULT_COLUMN_VISIBILITY } from "./columns";

const ENTRIES_TABLE_LAYOUT_VERSION = 1;
const STORAGE_KEY_PREFIX = "hourglass:entries:table-layout:v1";
const DEFAULT_COLUMN_MIN_SIZE = 40;
const DEFAULT_COLUMN_MAX_SIZE = 800;

export type EntriesTableColumnSizingBounds = Record<
	string,
	{ minSize?: number; maxSize?: number }
>;

export interface EntriesTableLayout {
	version: typeof ENTRIES_TABLE_LAYOUT_VERSION;
	columnVisibility: VisibilityState;
	columnOrder: ColumnOrderState;
	columnSizing: ColumnSizingState;
}

const DISPLAY_COLUMN_IDS = new Set(DEFAULT_COLUMN_ORDER);
const FORCED_HIDDEN_COLUMN_IDS = new Set(
	Object.keys(DEFAULT_COLUMN_VISIBILITY),
);

function sanitizeEmail(email: string): string {
	return email
		.trim()
		.toLowerCase()
		.replace(/[^a-zA-Z0-9@._-]/g, "_");
}

export function getEntriesTableLayoutStorageKey(email: string): string | null {
	const sanitized = sanitizeEmail(email);
	if (!sanitized) return null;
	return `${STORAGE_KEY_PREFIX}:${sanitized}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeColumnVisibility(value: unknown): VisibilityState {
	const next: VisibilityState = { ...DEFAULT_COLUMN_VISIBILITY };
	if (!isRecord(value)) return next;

	for (const [key, rawValue] of Object.entries(value)) {
		if (!DISPLAY_COLUMN_IDS.has(key) || typeof rawValue !== "boolean") continue;
		next[key] = rawValue;
	}

	for (const key of FORCED_HIDDEN_COLUMN_IDS) {
		next[key] = false;
	}

	return next;
}

function normalizeColumnOrder(value: unknown): ColumnOrderState {
	const seen = new Set<string>();
	const out: string[] = [];
	const order = Array.isArray(value) ? value : [];

	for (const id of order) {
		if (typeof id !== "string" || !DISPLAY_COLUMN_IDS.has(id) || seen.has(id)) {
			continue;
		}
		seen.add(id);
		out.push(id);
	}

	for (const id of DEFAULT_COLUMN_ORDER) {
		if (!seen.has(id)) out.push(id);
	}

	return out;
}

function clampSize(
	size: number,
	bounds: { minSize?: number; maxSize?: number },
): number {
	const minSize =
		typeof bounds.minSize === "number"
			? bounds.minSize
			: DEFAULT_COLUMN_MIN_SIZE;
	const maxSize =
		typeof bounds.maxSize === "number"
			? bounds.maxSize
			: DEFAULT_COLUMN_MAX_SIZE;
	return Math.min(maxSize, Math.max(minSize, size));
}

export function sanitizeEntriesTableColumnSizing(
	value: unknown,
	bounds: EntriesTableColumnSizingBounds,
): ColumnSizingState {
	const next: ColumnSizingState = {};
	if (!isRecord(value)) return next;

	for (const [key, rawValue] of Object.entries(value)) {
		const columnBounds = bounds[key];
		if (
			!columnBounds ||
			typeof rawValue !== "number" ||
			!Number.isFinite(rawValue)
		) {
			continue;
		}
		next[key] = clampSize(rawValue, columnBounds);
	}

	return next;
}

export function sanitizeEntriesTableLayout(
	value: unknown,
	bounds: EntriesTableColumnSizingBounds,
): EntriesTableLayout | null {
	if (!isRecord(value) || value.version !== ENTRIES_TABLE_LAYOUT_VERSION) {
		return null;
	}

	return {
		version: ENTRIES_TABLE_LAYOUT_VERSION,
		columnVisibility: normalizeColumnVisibility(value.columnVisibility),
		columnOrder: normalizeColumnOrder(value.columnOrder),
		columnSizing: sanitizeEntriesTableColumnSizing(value.columnSizing, bounds),
	};
}

export function readEntriesTableLayout(
	storageKey: string | null,
	bounds: EntriesTableColumnSizingBounds,
): EntriesTableLayout | null {
	if (!storageKey) return null;

	try {
		const stored = localStorage.getItem(storageKey);
		if (stored === null) return null;
		const layout = sanitizeEntriesTableLayout(JSON.parse(stored), bounds);
		if (!layout) localStorage.removeItem(storageKey);
		return layout;
	} catch {
		try {
			localStorage.removeItem(storageKey);
		} catch {
			// Ignore storage failures; the table can still use in-memory state.
		}
		return null;
	}
}

export function writeEntriesTableLayout(
	storageKey: string | null,
	layout: EntriesTableLayout,
	bounds: EntriesTableColumnSizingBounds,
): EntriesTableLayout {
	const sanitized = sanitizeEntriesTableLayout(layout, bounds) ?? {
		version: ENTRIES_TABLE_LAYOUT_VERSION,
		columnVisibility: normalizeColumnVisibility(layout.columnVisibility),
		columnOrder: normalizeColumnOrder(layout.columnOrder),
		columnSizing: sanitizeEntriesTableColumnSizing(layout.columnSizing, bounds),
	};

	if (!storageKey) return sanitized;

	try {
		localStorage.setItem(storageKey, JSON.stringify(sanitized));
	} catch {
		// Ignore storage failures; the table can still use in-memory state.
	}

	return sanitized;
}

export function clearEntriesTableLayout(storageKey: string | null): void {
	if (!storageKey) return;
	try {
		localStorage.removeItem(storageKey);
	} catch {
		// Ignore storage failures.
	}
}
